import React, { memo, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import classNames from 'classnames';
import { showNotification } from '../../redux/notification';
import utils from '../../utils/utils';
import guaranteedIcon from '../../assets/images/guaranteed-icon.svg';
import CompassIcon from '../../components/icons/CompassIcon';
import BaseBadge from '../../components/badges/BaseBadge';
import xhr from '../../utils/xhr';
import LoadingSpinner from '../../components/spinner/LoadingSpinner';

import './ShiftsConfirmation.scss';

const ShiftsConfirmation = (props) => {
	const dispatch = useDispatch();
	const [hasGuaranteedSlot, setHasGuaranteedSlot] = useState(false);
	const [similarShifts, setSimilarShifts] = useState([]);
	const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
	const getOpenSlotsByShift = (slots) => slots.filter((slot) => !slot.caregiver_id);

	const getSameDayAndStructureShifts = (shiftDate, shiftStructure) => {
		const openShifts = [];
		similarShifts.forEach((day) => {
			const dayDate = moment(day.raw_calendar_date).format('MMMM DD');
			if (dayDate === shiftDate) {
				day.shifts.forEach((shift) => {
					if (shift.shift_structure_id === shiftStructure.id && !shift.isRecurring) {
						openShifts.push(shift);
					}
				});
			}
		});

		return openShifts;
	};

	const getOpenShiftsSlotsBeforePosting = (shiftDate, shiftStructure) => {
		const openShiftSlots = [];
		const sameShifts = getSameDayAndStructureShifts(shiftDate, shiftStructure);

		for (let i = 0; i < sameShifts.length; i++) {
			const shift = sameShifts[i];
			openShiftSlots.push(...getOpenSlotsByShift(shift.slots));
		}

		return openShiftSlots;
	};

	useEffect(() => {
		setHasGuaranteedSlot(props.shiftsGroups.some((shift) => !!shift.is_guaranteed));
	}, [props.shiftsGroups]);

	const getSimilarShifts = useCallback(async () => {
		setShowLoadingSpinner(true);
		const shiftStructuresByDateParams = new URLSearchParams();

		props.shiftsGroups.map((shift) =>
			shift.dates.map((date) =>
				shiftStructuresByDateParams.append(moment(date).format('YYYY-MM-DD'), shift.shiftStructureId)
			)
		);

		try {
			const response = await xhr.request(
				'GET',
				`/facility/${props.facilityId}/shifts/similar?${shiftStructuresByDateParams.toString()}`
			);
			setSimilarShifts(response.data);
			setShowLoadingSpinner(false);
		} catch (e) {
			const errorMessage = utils.handleNetworkErrorMessage(e, 'Failed getting similar shifts. Please try again.');
			dispatch(showNotification('error', errorMessage));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, props.shiftsGroups]);

	useEffect(() => {
		getSimilarShifts();
	}, []);

	return showLoadingSpinner ? (
		<LoadingSpinner />
	) : (
		<table className="table shifts-confirmation-table">
			<thead className="header-confirmation-shifts">
				<tr>
					<th className="head-text resource-type">Shift Type</th>
					<th className="head-text">Date</th>
					<th className="head-text start-end">Start - End</th>
					{props.showUnits && <th className="head-text wrap">Unit</th>}
					<th className="head-text wrap">Shifts Requested</th>
					{!props.isHybrid && !props.isOnCall && (
						<>
							<th className="head-text wrap">Open Shifts Before Posting</th>
							<th className="head-text wrap">Total Open Shifts After Posting</th>
						</>
					)}
				</tr>
			</thead>
			<tbody>
				{props.shiftsGroups.map((shift, groupIndex) =>
					shift.dates.map((date, dateIndex) => {
						const shiftStructure = props.shiftStructures.find((structure) => structure.id === shift.shiftStructureId);
						const end = moment.utc(shiftStructure.to_time, 'HH:mm:ss');
						const start = moment.utc(shiftStructure.from_time, 'HH:mm:ss');

						if (end.isBefore(start)) {
							end.add(1, 'days');
						}

						const monthAndDay = moment(date).format('MM/DD');
						const dateToCheck = moment(date).format('MMMM DD');
						const openShiftsSlotsBeforePosting = getOpenShiftsSlotsBeforePosting(dateToCheck, shiftStructure);
						const [prevShiftsGuaranteed, prevShiftsNotGuaranteed] = openShiftsSlotsBeforePosting.reduce(
							([guaranteed, notGuaranteed], slot) => {
								if (slot.is_guaranteed) {
									return [guaranteed + 1, notGuaranteed];
								}
								return [guaranteed, notGuaranteed + 1];
							},
							[0, 0]
						);

						const [newShiftsGuaranteed, newShiftsNotGuaranteed] = shift.isGuaranteed
							? [shift.numberOfProviders, 0]
							: [0, shift.numberOfProviders];

						const totalOpenGuaranteed = prevShiftsGuaranteed + newShiftsGuaranteed;
						const totalOpenNotGuaranteed = prevShiftsNotGuaranteed + newShiftsNotGuaranteed;

						let { unitName } = shift;
						const limit = 34;
						if (shift.unitName?.length > limit) {
							unitName = `${shift.unitName.substring(0, limit)}...`;
						}

						return (
							<tr
								// eslint-disable-next-line react/no-array-index-key
								key={`confirmation-details-${groupIndex}-${dateIndex}`}
								className={classNames('row-confirmation-shifts request', {
									'first-row': dateIndex === 0
								})}
							>
								<td className="row-shift">{shiftStructure.resource_type}</td>
								<td className="row-shift">
									{monthAndDay} - {moment(date).format('ddd')}
								</td>
								<td className="row-shift">
									{start.format('h:mm A')} - {end.format('h:mm A')}
								</td>
								{props.showUnits && (
									<td className="row-shift">
										{shift.unitName ? (
											<OverlayTrigger
												placement="top"
												overlay={
													<Tooltip delay={{ show: 300, hide: 400 }} className="unit-tooltip">
														{shift.unitName}
													</Tooltip>
												}
											>
												<div>{unitName}</div>
											</OverlayTrigger>
										) : (
											<span>-</span>
										)}
									</td>
								)}
								<td
									className={classNames('row-shift', {
										'has-guaranteed': hasGuaranteedSlot,
										'last-column': props.isHybrid || props.isOnCall
									})}
								>
									{shiftStructure.is_orientation && (
										<BaseBadge badgeCopy="Orientation" className="orientation-badge" icon={<CompassIcon />} />
									)}
									{newShiftsGuaranteed > 0 && (
										<div className="shift-count-details">
											<span className="guaranteed-badge-span">
												<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
												Guaranteed: <span className="highlighted">{newShiftsGuaranteed}</span>
											</span>
										</div>
									)}
									{newShiftsNotGuaranteed > 0 && (
										<div className="shift-count-details">
											<span>
												Not Guaranteed: <span className="highlighted">{newShiftsNotGuaranteed}</span>
											</span>
										</div>
									)}
								</td>
								{!props.isHybrid && !props.isOnCall && (
									<>
										<td className="row-shift">
											{prevShiftsGuaranteed >= 0 && (
												<>
													<div className="shift-count-details">
														<span className="guaranteed-badge-span">
															<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
															Guaranteed:
														</span>
														<span className="highlighted">{prevShiftsGuaranteed}</span>
													</div>
													{prevShiftsNotGuaranteed > 0 && <div className="divider" />}
												</>
											)}
											{prevShiftsNotGuaranteed > 0 && (
												<div className="shift-count-details">
													<span>
														Not Guaranteed: <span className="highlighted">{prevShiftsNotGuaranteed}</span>
													</span>
												</div>
											)}
										</td>
										<td className="row-shift last-column">
											{totalOpenGuaranteed > 0 && (
												<>
													<div className="shift-count-details">
														<span className="guaranteed-badge-span">
															<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
															Guaranteed: <span className="highlighted">{totalOpenGuaranteed}</span>
														</span>
													</div>
													{totalOpenNotGuaranteed > 0 && <div className="divider" />}
												</>
											)}
											{totalOpenNotGuaranteed > 0 && (
												<div className="shift-count-details">
													<span>
														Not Guaranteed: <span className="highlighted">{totalOpenNotGuaranteed}</span>
													</span>
												</div>
											)}
										</td>
									</>
								)}
							</tr>
						);
					})
				)}
			</tbody>
		</table>
	);
};

export default memo(ShiftsConfirmation);
